import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from '../../BaseComponents/Modal';
import { uploadMemberReport } from '../../../api/memberReport';
import './UploadModal.scss';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ isOpen, onClose, onUploadSuccess }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const location = useLocation();

  const validateFile = (file: File): string | null => {
    const allowedMimeTypes = new Set([
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/zip',
      'application/x-zip-compressed'
    ]);
    const lowerName = file.name.toLowerCase();
    const allowedExtensions = /\.(pdf|doc|docx|zip)$/i;
    const isAllowed =
      (file.type && allowedMimeTypes.has(file.type)) || allowedExtensions.test(lowerName);

    if (!isAllowed) {
      return 'Please select a valid file (PDF, DOC, DOCX, or ZIP)';
    }
    if (file.size > 20 * 1024 * 1024) {
      return 'File size must be less than 20MB';
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }
    setError(null);
    setSelectedFile(file);
    if (!name.trim()) {
      setName(file.name.replace(/\.(pdf|doc|docx|zip)$/i, ''));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a PDF, DOC, DOCX, or ZIP file');
      return;
    }
    if (!name.trim()) {
      setError('Please enter a report name');
      return;
    }
    if (!reportDate) {
      setError('Please select a report date');
      return;
    }

    try {
      setIsUploading(true);
      setError(null);
      const memberId = location.pathname.split('/')[2];
      if (!memberId) {
        throw new Error('Member ID not found in URL');
      }

      await uploadMemberReport(memberId, {
        file: selectedFile,
        name: name.trim(),
        status: 'UPLOADED',
        report_date: reportDate,
        report_type: 'dexa_scan'
      });

      setSelectedFile(null);
      setName('');
      setReportDate(new Date().toISOString().slice(0, 10));
      onUploadSuccess();
      onClose();
    } catch (uploadError: any) {
      setError(uploadError?.message || 'Failed to upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setError(null);
      setSelectedFile(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title='Upload DEXA Report' width='600px'>
      <div className='upload-modal dexa-upload-modal'>
        <div className='form-field'>
          <label htmlFor='dexa-report-name'>Name</label>
          <input
            id='dexa-report-name'
            type='text'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='Report name'
            disabled={isUploading}
          />
        </div>

        <div className='form-field'>
          <label htmlFor='dexa-report-date'>Date</label>
          <input
            id='dexa-report-date'
            type='date'
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            disabled={isUploading}
          />
        </div>

        <div
          className={`upload-area ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files[0];
            if (file) handleFileSelect(file);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragOver(false);
          }}
          onClick={() => {
            if (!isUploading) fileInputRef.current?.click();
          }}
        >
          <div className='upload-icon'>
            {isUploading ? <div className='loading-spinner'></div> : <span>📄</span>}
          </div>
          <div className='upload-text'>
            {isUploading ? (
              <p className='primary-text'>Uploading file...</p>
            ) : selectedFile ? (
              <>
                <p className='primary-text'>{selectedFile.name}</p>
                <p className='secondary-text'>Click to choose a different file</p>
              </>
            ) : (
              <>
                <p className='primary-text'>Click to upload DEXA report</p>
                <p className='secondary-text'>or drag and drop your file here</p>
                <p className='file-types'>PDF, DOC, DOCX, or ZIP</p>
              </>
            )}
          </div>
        </div>

        {error && <div className='error-message'>{error}</div>}

        <input
          ref={fileInputRef}
          type='file'
          accept='.pdf,.doc,.docx,.zip,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/zip,application/x-zip-compressed'
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
          style={{ display: 'none' }}
          disabled={isUploading}
        />

        <div className='upload-actions'>
          <button type='button' className='cancel-btn' onClick={handleClose} disabled={isUploading}>
            Cancel
          </button>
          <button
            type='button'
            className='submit-btn'
            onClick={handleUpload}
            disabled={isUploading || !selectedFile}
          >
            {isUploading ? 'Uploading...' : 'Upload'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default UploadModal;
